"use client"

import type React from "react"
import { useState, useEffect, useCallback } from "react"
import {
  Container,
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
  Divider,
  Card,
  CardHeader,
  CardContent,
  Grid,
  Paper,
  Avatar,
  IconButton,
  Fab,
  CircularProgress,
  Alert,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  useTheme,
  useMediaQuery,
  Skeleton,
  alpha,
} from "@mui/material"
import {
  Add,
  Close,
  AttachFile,
  Description,
  GetApp,
  Image,
  PictureAsPdf,
  InsertDriveFile,
  CalendarToday,
  AttachMoney,
  TrendingUp,
  ReceiptLong,
} from "@mui/icons-material"
import { DatePicker } from "@mui/x-date-pickers"
import { styled } from "@mui/material/styles"
import { createRequisition, getMyRequisitions } from "../../../api/requisition/requisition"
import { profileImgUrl, requestID } from "../../../common/utils"
import { IonPage, IonContent } from "@ionic/react"
import { formatCurrencyN } from "../../../common/utils_t"

interface Requisition {
  id: string
  requisition_id: string
  title: string
  description: string
  amount: string
  status: string
  expense_date: string
  category: string
  attachment_path?: string
  created_at: string
  manager_notes?: string
}

interface RequisitionData {
  title: string
  description: string
  amount: string
  expense_date: Date | null
  category: string
  attachment: File[] | null
}

// Enhanced styled components with modern theme
const StyledFab = styled(Fab)(({ theme }) => ({
  position: "fixed",
  bottom: theme.spacing(3),
  right: theme.spacing(3),
  zIndex: 1000,
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  boxShadow: `0 8px 25px -6px ${alpha(theme.palette.primary.main, 0.5)}`,
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: `0 12px 30px -6px ${alpha(theme.palette.primary.main, 0.6)}`,
  },
  transition: "all 0.3s ease",
}))

const StatusChip = styled(Chip)<{ status: string }>(({ theme, status }) => ({
  fontWeight: 700,
  borderRadius: 12,
  textTransform: "capitalize",
  ...(status === "approve" && {
    background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
    color: theme.palette.success.contrastText,
    boxShadow: `0 4px 15px ${alpha(theme.palette.success.main, 0.3)}`,
  }),
  ...(status === "rejected" && {
    background: `linear-gradient(45deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
    color: theme.palette.error.contrastText,
    boxShadow: `0 4px 15px ${alpha(theme.palette.error.main, 0.3)}`,
  }),
  ...(status === "pending" && {
    background: `linear-gradient(45deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
    color: theme.palette.warning.contrastText,
    boxShadow: `0 4px 15px ${alpha(theme.palette.warning.main, 0.3)}`,
  }),
  ...(status === "postponed" && {
    background: `linear-gradient(45deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
    color: theme.palette.info.contrastText,
    boxShadow: `0 4px 15px ${alpha(theme.palette.info.main, 0.3)}`,
  }),
}))

const RequisitionCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  cursor: "pointer",
  borderRadius: 16,
  background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.default, 0.8)} 100%)`,
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: `0 20px 40px ${alpha(theme.palette.common.black, 0.1)}`,
    borderColor: alpha(theme.palette.primary.main, 0.3),
  },
}))

const AttachmentPreview = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  textAlign: "center",
  cursor: "pointer",
  borderRadius: 12,
  background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`,
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "scale(1.05)",
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
    boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.15)}`,
  },
}))

const GradientAppBar = styled(AppBar)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  boxShadow: "none",
  borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
}))

const CategoryChip = styled(Chip)(({ theme }) => ({
  background: `linear-gradient(45deg, ${alpha(theme.palette.secondary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.light, 0.05)} 100%)`,
  color: theme.palette.secondary.main,
  border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
  borderRadius: 8,
  fontWeight: 600,
}))

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    transition: "all 0.3s ease",
    "&:hover": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: alpha(theme.palette.primary.main, 0.6),
      },
    },
    "&.Mui-focused": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
      },
    },
  },
}))

const categories = ["Office Supplies", "Travel", "Equipment", "Software", "Training", "Marketing", "Utilities", "Other"]

const CreateRequisitionPage: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"))

  const [requisitions, setRequisitions] = useState<Requisition[]>([])
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [selectedRequisition, setSelectedRequisition] = useState<Requisition | null>(null)
  const [loading, setLoading] = useState(false)
  const [formLoading, setFormLoading] = useState(false)
  const [formData, setFormData] = useState<RequisitionData>({
    title: "",
    description: "",
    amount: "",
    expense_date: null,
    category: "",
    attachment: [],
  })

  useEffect(() => {
    fetchRequisitions()
  }, [])

  const fetchRequisitions = async () => {
    setLoading(true)
    try {
      const { data } = await getMyRequisitions()
      setRequisitions(data)
    } catch (error) {
      console.error("Error fetching requisitions:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleViewRequisition = (req: Requisition) => {
    setSelectedRequisition(req)
    setShowViewModal(true)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSelectChange = (field: string, value: any) => {
    setFormData({ ...formData, [field]: value })
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files)
      setFormData({ ...formData, attachment: files })
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormLoading(true)

    try {
      const formDataToSend = new FormData()
      formDataToSend.append("requestID", requestID())
      formDataToSend.append("title", formData.title)
      formDataToSend.append("description", formData.description)
      formDataToSend.append("amount", formData.amount)
      formDataToSend.append("expense_date", formData.expense_date?.toISOString().split("T")[0] || "")
      formDataToSend.append("category", formData.category)

      if (formData.attachment) {
        formData.attachment.forEach((file) => {
          formDataToSend.append("attachments[]", file)
        })
      }

      await createRequisition(formDataToSend)
      setShowCreateModal(false)
      setFormData({
        title: "",
        description: "",
        amount: "",
        expense_date: null,
        category: "",
        attachment: null,
      })
      await fetchRequisitions()
    } catch (error) {
      console.error("Error creating requisition:", error)
    } finally {
      setFormLoading(false)
    }
  }

  const getStatusColor = useCallback((status: string) => {
    switch (status) {
      case "approve":
        return "success"
      case "rejected":
        return "error"
      case "pending":
        return "warning"
      case "postponed":
        return "info"
      default:
        return "primary"
    }
  }, [])

  const parseAttachments = (attachmentPath?: string) => {
    if (!attachmentPath) return []
    try {
      return JSON.parse(attachmentPath) as string[]
    } catch {
      return []
    }
  }

  const getFileIcon = (filename: string) => {
    const extension = filename.toLowerCase().split(".").pop()
    switch (extension) {
      case "pdf":
        return <PictureAsPdf color="error" />
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
        return <Image color="primary" />
      default:
        return <InsertDriveFile color="action" />
    }
  }

  const renderSkeletonLoader = () => (
    <Grid container spacing={3}>
      {[1, 2, 3, 4, 5, 6].map((item) => (
        <Grid item xs={12} sm={6} md={4} key={item}>
          <Card sx={{ borderRadius: 3, mb: 2 }}>
            <CardContent>
              <Skeleton variant="rectangular" width="100%" height={160} sx={{ borderRadius: 2, mb: 2 }} />
              <Skeleton variant="text" width="60%" height={32} />
              <Skeleton variant="text" width="40%" height={24} sx={{ mt: 1 }} />
              <Skeleton variant="text" width="30%" height={24} />
              <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
                <Skeleton variant="text" width="25%" height={24} />
                <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: 2 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  )

  const StatCard = ({ icon, label, value, color }: { icon: React.ReactNode; label: string; value: string; color: string }) => (
    <Paper
      sx={{
        p: 3,
        borderRadius: 3,
        background: `linear-gradient(135deg, ${alpha(color, 0.1)} 0%, ${alpha(color, 0.05)} 100%)`,
        border: `1px solid ${alpha(color, 0.2)}`,
        textAlign: "center",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 2 }}>
        <Avatar sx={{ bgcolor: alpha(color, 0.2), color: color, width: 56, height: 56 }}>
          {icon}
        </Avatar>
      </Box>
      <Typography variant="h4" fontWeight="bold" color={color} gutterBottom>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Paper>
  )

  return (
    <IonPage className="pushDown">
      <IonContent>
        <Box sx={{ minHeight: "100vh", background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.primary.light, 0.02)} 100%)` }}>
          {/* Enhanced Header */}
         <GradientAppBar position="sticky">
  <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <Avatar sx={{ bgcolor: alpha(theme.palette.common.white, 0.2), width: 40, height: 40 }}>
        <ReceiptLong />
      </Avatar>
      <Typography variant="h6" component="div" sx={{ fontWeight: 700 }}>
        My Requisitions
      </Typography>
    </Box>

    {!isMobile && (
      <Button
        variant="contained"
        startIcon={<Add />}
        onClick={() => setShowCreateModal(true)}
        sx={{ /* your styles */ }}
      >
        New Requisition
      </Button>
    )}
  </Toolbar>
</GradientAppBar>


          {/* Main Content */}
          <Container maxWidth="xl" sx={{ py: 4 }}>
            {/* Statistics Row */}
            {!loading && requisitions.length > 0 && (
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard
                    icon={<ReceiptLong />}
                    label="Total Requisitions"
                    value={requisitions.length.toString()}
                    color={theme.palette.primary.main}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard
                    icon={<TrendingUp />}
                    label="approve"
                    value={requisitions.filter(r => r.status === "approve").length.toString()}
                    color={theme.palette.success.main}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard
                    icon={<Description />}
                    label="Pending"
                    value={requisitions.filter(r => r.status === "pending").length.toString()}
                    color={theme.palette.warning.main}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard
                    icon={<Close />}
                    label="Rejected"
                    value={requisitions.filter(r => r.status === "rejected").length.toString()}
                    color={theme.palette.error.main}
                  />
                </Grid>
              </Grid>
            )}

            {loading ? (
              renderSkeletonLoader()
            ) : requisitions.length === 0 ? (
              <Paper
                sx={{
                  p: 8,
                  textAlign: "center",
                  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.default, 0.8)} 100%)`,
                  borderRadius: 4,
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  boxShadow: `0 20px 40px ${alpha(theme.palette.common.black, 0.05)}`,
                }}
              >
                <Description sx={{ fontSize: 96, color: alpha(theme.palette.text.secondary, 0.3), mb: 3 }} />
                <Typography variant="h4" gutterBottom color="text.secondary" fontWeight="300">
                  No Requisitions Yet
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: "auto" }}>
                  Create your first requisition to get started with expense management and approval workflows.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setShowCreateModal(true)}
                  size="large"
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.3)}`,
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: `0 12px 35px ${alpha(theme.palette.primary.main, 0.4)}`,
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Create New Requisition
                </Button>
              </Paper>
            ) : (
              <Grid container spacing={3}>
                {requisitions.map((req) => (
                  <Grid item xs={12} sm={6} md={4} key={req.id}>
                    <RequisitionCard onClick={() => handleViewRequisition(req)}>
                      <CardHeader
                        avatar={
                          <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                            <AttachMoney />
                          </Avatar>
                        }
                        action={<StatusChip label={req.status} status={req.status} size="small" />}
                        title={
                          <Typography variant="h6" noWrap fontWeight="600">
                            {req.title}
                          </Typography>
                        }
                        subheader={
                          <Typography variant="caption" color="text.secondary">
                            {new Date(req.created_at).toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </Typography>
                        }
                      />
                      <CardContent sx={{ pt: 0 }}>
                        <Stack spacing={2}>
                          <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom fontWeight="500">
                              Description
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                color: "text.primary",
                                lineHeight: 1.5,
                              }}
                            >
                              {req.description}
                            </Typography>
                          </Box>

                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary" display="block" fontWeight="500">
                                Amount
                              </Typography>
                              <Typography variant="h6" color="primary.main" fontWeight="700">
                                {formatCurrencyN(Number(req.amount))}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary" display="block" fontWeight="500">
                                Category
                              </Typography>
                              <CategoryChip label={req.category} size="small" />
                            </Grid>
                          </Grid>

                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, pt: 1 }}>
                            <CalendarToday sx={{ fontSize: 16, color: "text.secondary" }} />
                            <Typography variant="caption" color="text.secondary">
                              Expense Date: {new Date(req.expense_date).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </RequisitionCard>
                  </Grid>
                ))}
              </Grid>
            )}
          </Container>

          {/* Enhanced Floating Action Button */}
          {isMobile && (
            <StyledFab color="primary" onClick={() => setShowCreateModal(true)}>
              <Add />
            </StyledFab>
          )}

          {/* Enhanced Create Requisition Modal */}
          <Dialog
            open={showCreateModal}
            onClose={() => setShowCreateModal(false)}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
              sx: {
                borderRadius: isMobile ? 0 : 4,
                background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.default, 0.9)} 100%)`,
              },
            }}
          >
            <DialogTitle sx={{ pb: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h5" fontWeight="700">
                  Create New Requisition
                </Typography>
                <IconButton 
                  onClick={() => setShowCreateModal(false)}
                  sx={{
                    color: theme.palette.text.secondary,
                    "&:hover": {
                      color: theme.palette.text.primary,
                      bgcolor: alpha(theme.palette.text.primary, 0.1),
                    },
                  }}
                >
                  <Close />
                </IconButton>
              </Stack>
            </DialogTitle>

            <form onSubmit={handleSubmit}>
              <DialogContent dividers sx={{ border: "none" }}>
                <Stack spacing={3}>
                  <StyledTextField
                    name="title"
                    label="Title"
                    value={formData.title}
                    onChange={handleChange}
                    fullWidth
                    required
                    variant="outlined"
                  />

                  <StyledTextField
                    name="description"
                    label="Description"
                    value={formData.description}
                    onChange={handleChange}
                    fullWidth
                    required
                    multiline
                    rows={4}
                    variant="outlined"
                  />

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        name="amount"
                        label="Amount"
                        value={formData.amount}
                        onChange={handleChange}
                        fullWidth
                        required
                        type="number"
                        InputProps={{
                          startAdornment: <AttachMoney sx={{ color: "text.secondary", mr: 1 }} />,
                        }}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth required>
                        <InputLabel>Category</InputLabel>
                        <Select
                          value={formData.category}
                          label="Category"
                          onChange={(e) => handleSelectChange("category", e.target.value)}
                          sx={{ borderRadius: 2 }}
                        >
                          {categories.map((category) => (
                            <MenuItem key={category} value={category}>
                              {category}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>

                  <DatePicker
                    label="Expense Date"
                    value={formData.expense_date}
                    onChange={(date) => handleSelectChange("expense_date", date)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                      },
                    }}
                  />

                  <Box>
                    <Typography variant="subtitle1" gutterBottom fontWeight="600">
                      Attachments
                    </Typography>
                    <Button 
                      variant="outlined" 
                      component="label" 
                      startIcon={<AttachFile />} 
                      fullWidth 
                      sx={{ 
                        mb: 2,
                        borderRadius: 2,
                        py: 1.5,
                        borderStyle: "dashed",
                      }}
                    >
                      Choose Files
                      <input type="file" hidden multiple onChange={handleFileChange} />
                    </Button>

                    {formData.attachment && formData.attachment.length > 0 && (
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Selected Files:
                        </Typography>
                        <Stack spacing={1}>
                          {formData.attachment.map((file, index) => (
                            <Chip
                              key={index}
                              label={file.name}
                              variant="outlined"
                              icon={getFileIcon(file.name)}
                              onDelete={() => {
                                const newFiles = formData.attachment?.filter((_, i) => i !== index) || []
                                setFormData({ ...formData, attachment: newFiles })
                              }}
                              sx={{ borderRadius: 1 }}
                            />
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </Box>
                </Stack>
              </DialogContent>

              <DialogActions sx={{ p: 3, gap: 2 }}>
                <Button 
                  onClick={() => setShowCreateModal(false)} 
                  disabled={formLoading}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={formLoading}
                  startIcon={formLoading ? <CircularProgress size={20} /> : <Add />}
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    py: 1,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.3)}`,
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: `0 12px 35px ${alpha(theme.palette.primary.main, 0.4)}`,
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  {formLoading ? "Creating..." : "Create Requisition"}
                </Button>
              </DialogActions>
            </form>
          </Dialog>

          {/* Enhanced View Requisition Modal */}
          <Dialog
            open={showViewModal}
            onClose={() => setShowViewModal(false)}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
              sx: {
                borderRadius: isMobile ? 0 : 4,
                background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.default, 0.9)} 100%)`,
              },
            }}
          >
            <DialogTitle sx={{ pb: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h5" fontWeight="700">
                  Requisition Details
                </Typography>
                <IconButton 
                  onClick={() => setShowViewModal(false)}
                  sx={{
                    color: theme.palette.text.secondary,
                    "&:hover": {
                      color: theme.palette.text.primary,
                      bgcolor: alpha(theme.palette.text.primary, 0.1),
                    },
                  }}
                >
                  <Close />
                </IconButton>
              </Stack>
            </DialogTitle>

            <DialogContent dividers sx={{ border: "none" }}>
              {selectedRequisition && (
                <Stack spacing={4}>
                  <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
                      <Typography variant="h4" fontWeight="700" sx={{ maxWidth: "70%" }}>
                        {selectedRequisition.title}
                      </Typography>
                      <StatusChip label={selectedRequisition.status} status={selectedRequisition.status} />
                    </Stack>
                    <Divider />
                  </Box>

                  <Box>
                    <Typography variant="h6" color="text.secondary" gutterBottom fontWeight="600">
                      Description
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                      {selectedRequisition.description}
                    </Typography>
                  </Box>

                  <Grid container spacing={4}>
                    <Grid item xs={12} sm={6}>
                      <Paper sx={{ p: 3, borderRadius: 3, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                        <Typography variant="subtitle1" color="text.secondary" gutterBottom fontWeight="600">
                          Amount
                        </Typography>
                        <Typography variant="h3" color="primary.main" fontWeight="800">
                          {formatCurrencyN(Number(selectedRequisition.amount))}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Stack spacing={3}>
                        <Box>
                          <Typography variant="subtitle1" color="text.secondary" gutterBottom fontWeight="600">
                            Expense Date
                          </Typography>
                          <Typography variant="body1" fontWeight="500">
                            {new Date(selectedRequisition.expense_date).toLocaleDateString()}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle1" color="text.secondary" gutterBottom fontWeight="600">
                            Category
                          </Typography>
                          <CategoryChip label={selectedRequisition.category} />
                        </Box>
                        <Box>
                          <Typography variant="subtitle1" color="text.secondary" gutterBottom fontWeight="600">
                            Created
                          </Typography>
                          <Typography variant="body2">
                            {new Date(selectedRequisition.created_at).toLocaleString()}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  </Grid>

                  {selectedRequisition.manager_notes && (
                    <Box>
                      <Typography variant="h6" color="text.secondary" gutterBottom fontWeight="600">
                        Manager Notes
                      </Typography>
                      <Alert 
                        severity="info" 
                        sx={{ 
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.info.main, 0.1),
                          color: theme.palette.info.dark,
                        }}
                      >
                        {selectedRequisition.manager_notes}
                      </Alert>
                    </Box>
                  )}

                  <Divider />

                  <Box>
                    <Typography variant="h6" color="text.secondary" gutterBottom fontWeight="600">
                      Attachments
                    </Typography>
                    {parseAttachments(selectedRequisition.attachment_path).length > 0 ? (
                      <ImageList cols={isMobile ? 2 : 3} gap={16}>
                        {parseAttachments(selectedRequisition.attachment_path).map((path, index) => (
                          <ImageListItem key={index}>
                            <AttachmentPreview
                              onClick={() => window.open(`${profileImgUrl}${path}`, "_blank")}
                            >
                              {path.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                                <img
                                  src={`${profileImgUrl}${path}`}
                                  alt={`Attachment ${index + 1}`}
                                  style={{
                                    width: "100%",
                                    height: 120,
                                    objectFit: "cover",
                                    borderRadius: 8,
                                  }}
                                />
                              ) : (
                                <Box sx={{ py: 3 }}>
                                  {getFileIcon(path)}
                                  <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                                    {path.split("/").pop()}
                                  </Typography>
                                </Box>
                              )}
                              <ImageListItemBar
                                title={`Attachment ${index + 1}`}
                                actionIcon={
                                  <IconButton 
                                    sx={{ 
                                      color: "rgba(255, 255, 255, 0.9)",
                                      bgcolor: alpha(theme.palette.common.black, 0.5),
                                      "&:hover": {
                                        bgcolor: alpha(theme.palette.common.black, 0.7),
                                      },
                                    }}
                                  >
                                    <GetApp />
                                  </IconButton>
                                }
                              />
                            </AttachmentPreview>
                          </ImageListItem>
                        ))}
                      </ImageList>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                        No attachments available
                      </Typography>
                    )}
                  </Box>
                </Stack>
              )}
            </DialogContent>

            <DialogActions sx={{ p: 3 }}>
              <Button 
                onClick={() => setShowViewModal(false)}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1,
                }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </IonContent>
    </IonPage>
  )
}

export default CreateRequisitionPage